import { Card, CardContent } from "@/components/ui/card";
import type { ControlCenter } from "@/features/admin";
import { cn } from "@/lib/utils";
import { ArrowDownRightIcon, ArrowUpRightIcon } from "@phosphor-icons/react";
import { clock, compact, compareLabel } from "./format";

/**
 * Seven figures, each against a comparison window of the same length — the one just before
 * it, or yesterday up to the same time for "today". "Better" is not always "up":
 * a longer wait or more failures is a red arrow pointing up, which is why each tile says
 * which direction is good.
 */
export function Kpis({ data }: { data: ControlCenter }) {
  const { totals, previous, latency, timeline } = data;
  const rated = totals.success + totals.failure;
  const prevRated = previous.success + previous.failure;
  const successRate = rated ? totals.success / rated : null;
  const prevSuccessRate = prevRated ? previous.success / prevRated : null;
  const vs = compareLabel(data.compare);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3">
      <Kpi
        label="Conversations"
        value={compact(totals.sessions)}
        delta={trend(totals.sessions, previous.sessions)}
        deltaTitle={vs}
        good="up"
        spark={timeline.map((p) => p.calls + p.chats)}
        color="var(--chart-1)"
        hint={`${compact(totals.calls)} appels · ${compact(totals.chats)} chats`}
      />
      <Kpi
        label="Appels transférés au salon"
        value={compact(totals.calls_transferred)}
        delta={trend(totals.calls_transferred, previous.calls_transferred)}
        deltaTitle={vs}
        good="neutral"
        color="var(--chart-4)"
        hint={`${compact(totals.calls - totals.calls_transferred)} sans transfert · sur ${compact(totals.calls)} appels`}
      />
      <Kpi
        label="Taux de réussite"
        value={successRate === null ? "—" : `${Math.round(successRate * 100)} %`}
        delta={
          successRate !== null && prevSuccessRate !== null
            ? (successRate - prevSuccessRate) * 100
            : null
        }
        deltaUnit="pts"
        deltaTitle={vs}
        good="up"
        spark={timeline.map((p) => p.failures)}
        sparkLabel="échecs"
        color="var(--danger)"
        hint={`${totals.failure} échec(s) sur ${rated} qualifiée(s)`}
      />
      <Kpi
        label="Durée moyenne d'appel"
        value={totals.avg_call_ms ? clock(totals.avg_call_ms) : "—"}
        delta={trend(totals.avg_call_ms, previous.avg_call_ms)}
        deltaTitle={vs}
        good="neutral"
        color="var(--chart-2)"
        hint="Temps de parole de l'appelant"
      />
      <Kpi
        label="Échanges par conversation"
        value={totals.avg_exchanges ? totals.avg_exchanges.toFixed(1) : "—"}
        delta={trend(totals.avg_exchanges, previous.avg_exchanges)}
        deltaTitle={vs}
        good="neutral"
        color="var(--chart-3)"
        hint="Tours de parole du client"
      />
      <Kpi
        label="Temps de réponse"
        value={latency.samples ? `${(latency.p50 / 1000).toFixed(2)} s` : "—"}
        good="down"
        tone={
          // Same rule as the health signal (`latencyStatus`, control-center.ts).
          latency.p95 > 3000
            ? "bad"
            : latency.p50 < 1000 && latency.p95 < 2300
              ? undefined
              : "warn"
        }
        color="var(--chart-5)"
        hint={
          latency.samples
            ? `médiane · p95 ${(latency.p95 / 1000).toFixed(2)} s`
            : "Aucun tour mesuré"
        }
      />
      <Kpi
        label="Clients finaux"
        value={compact(totals.unique_customers)}
        delta={trend(totals.unique_customers, previous.unique_customers)}
        deltaTitle={vs}
        good="up"
        color="var(--chart-6)"
        hint={`sur ${totals.tenants} tenant(s) actif(s)`}
      />
    </div>
  );
}

const trend = (now: number, before: number): number | null =>
  before > 0 ? ((now - before) / before) * 100 : null;

function Kpi({
  label,
  value,
  hint,
  delta,
  deltaUnit = "%",
  deltaTitle,
  good,
  spark,
  sparkLabel,
  color,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  delta?: number | null;
  deltaUnit?: string;
  deltaTitle?: string;
  good: "up" | "down" | "neutral";
  spark?: number[];
  sparkLabel?: string;
  color: string;
  tone?: "warn" | "bad";
}) {
  const hasDelta = delta !== null && delta !== undefined && Math.abs(delta) >= 0.5;
  const isUp = (delta ?? 0) > 0;
  const deltaTone =
    good === "neutral"
      ? "text-muted-foreground"
      : isUp === (good === "up")
        ? "text-success"
        : "text-danger";

  return (
    <Card className="relative overflow-hidden">
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{ background: color }}
      />
      <CardContent className="p-4">
        <div className="text-xs font-medium text-muted-foreground truncate">
          {label}
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span
            className={cn(
              "text-2xl font-bold tabular-nums tracking-tight",
              tone === "bad" && "text-danger",
              tone === "warn" && "text-warning",
            )}
          >
            {value}
          </span>
          {hasDelta ? (
            <span
              className={cn(
                "inline-flex items-center text-xs font-medium tabular-nums",
                deltaTone,
              )}
              title={deltaTitle}
            >
              {isUp ? (
                <ArrowUpRightIcon size={12} weight="bold" />
              ) : (
                <ArrowDownRightIcon size={12} weight="bold" />
              )}
              {Math.abs(delta!).toFixed(deltaUnit === "pts" ? 1 : 0)}
              {deltaUnit === "pts" ? " pts" : " %"}
            </span>
          ) : null}
        </div>
        {hint ? (
          <div className="mt-0.5 text-xs text-muted-foreground truncate">
            {hint}
          </div>
        ) : null}
        {spark && spark.some((v) => v > 0) ? (
          <Sparkline values={spark} color={color} label={sparkLabel} />
        ) : null}
      </CardContent>
    </Card>
  );
}

/** A line with its area under it — drawn by hand, a chart library per tile is too much. */
function Sparkline({
  values,
  color,
  label,
}: {
  values: number[];
  color: string;
  label?: string;
}) {
  const w = 120;
  const h = 28;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const pts = values.map((v, i) => [i * step, h - 2 - (v / max) * (h - 4)]);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x},${y}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="mt-2 h-7 w-full"
      aria-label={label}
    >
      <path d={area} fill={color} fillOpacity={0.12} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
